import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Utensils } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';

const NutritionTab = ({ user, setResult }) => {
  const { t } = useTranslation();
  const [nutritionGoal, setNutritionGoal] = useState('');
  const [targetCalories, setTargetCalories] = useState('');
  const [dietaryRestrictions, setDietaryRestrictions] = useState([]);
  const [loading, setLoading] = useState(false);

  const goals = [
    { key: 'weightLoss', label: t('nutritionGoals.weightLoss') },
    { key: 'weightGain', label: t('nutritionGoals.weightGain') },
    { key: 'weightMaintenance', label: t('nutritionGoals.weightMaintenance') },
    { key: 'medicalManagement', label: t('nutritionGoals.medicalManagement') }
  ];

  const restrictions = [
    { key: 'vegetarian', label: t('dietaryRestrictions.vegetarian') },
    { key: 'vegan', label: t('dietaryRestrictions.vegan') },
    { key: 'glutenFree', label: t('dietaryRestrictions.glutenFree') },
    { key: 'dairyFree', label: t('dietaryRestrictions.dairyFree') },
    { key: 'nutFree', label: t('dietaryRestrictions.nutFree') },
    { key: 'jain', label: t('dietaryRestrictions.jain') }
  ];

  const handleNutritionAnalysis = async () => {
    if (!nutritionGoal) {
      toast.error(t('aiFeatures.pleaseSelectNutritionGoal'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/nutrition/meal-plan`, {
        goal: nutritionGoal,
        target_calories: targetCalories ? parseInt(targetCalories) : 2000,
        dietary_restrictions: dietaryRestrictions,
        preferences: { cuisine: 'indian', meals_per_day: 4 }
      });
      
      setResult({ type: 'nutrition', data: response.data });
      toast.success(t('aiFeatures.mealPlanGenerated'));
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          {t('aiFeatures.nutritionGoal')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {goals.map((goal) => (
            <button
              key={goal.key}
              onClick={() => setNutritionGoal(goal.key)}
              className={`p-4 rounded-lg border text-sm font-medium transition-all transform hover:scale-105 ${
                nutritionGoal === goal.key
                  ? 'bg-green-100 border-green-500 text-green-700 shadow-md'
                  : 'bg-white border-gray-300 hover:border-green-300'
              }`}
            >
              {goal.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {t('aiFeatures.targetCalories')}
        </label>
        <input
          type="number"
          value={targetCalories}
          onChange={(e) => setTargetCalories(e.target.value)}
          placeholder="2000"
          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          {t('aiFeatures.dietaryRestrictions')}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {restrictions.map((restriction) => (
            <button
              key={restriction.key}
              onClick={() => {
                if (dietaryRestrictions.includes(restriction.key)) {
                  setDietaryRestrictions(dietaryRestrictions.filter(r => r !== restriction.key));
                } else {
                  setDietaryRestrictions([...dietaryRestrictions, restriction.key]);
                }
              }}
              className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                dietaryRestrictions.includes(restriction.key)
                  ? 'bg-green-100 border-green-500 text-green-700'
                  : 'bg-white border-gray-300 hover:border-green-300'
              }`}
            >
              {restriction.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleNutritionAnalysis}
          disabled={loading || !nutritionGoal}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          {loading ? t('aiFeatures.generating') : t('aiFeatures.generateMealPlan')}
        </Button>
        <Button
          onClick={() => toast.info("Check your history in the dedicated page")}
          variant="outline"
          className="border-green-600 text-green-600"
        >
          {t('tabs.history')}
        </Button>
      </div>
    </div>
  );
};

export default NutritionTab;
